/**
 * Boolean idle-state hook for ambient effects.
 *
 * Returns a live boolean: true after `delayMs` of no input, back to false instantly on
 * any activity — matching the "clears on any input" requirement for effects.
 */
import { useEffect, useState } from 'react';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
] as const;

/**
 * Track whether the user has been idle for at least `delayMs`.
 *
 * @param delayMs - Inactivity duration before becoming idle.
 * @param enabled - When false, never reports idle and attaches no listeners.
 * @returns Whether the user is currently idle.
 */
export function useIsIdle(delayMs: number, enabled = true): boolean {
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      timer = setTimeout(() => setIdle(true), delayMs);
    };
    const onActivity = () => {
      setIdle(false);
      clearTimeout(timer);
      arm();
    };
    const onVisibility = () => {
      clearTimeout(timer);
      if (document.hidden) setIdle(false);
      else arm();
    };

    arm();
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(timer);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [delayMs, enabled]);

  // Derive the reported value so a disabled hook never reports a stale `true`
  // (and we avoid a setState-in-effect to reset it).
  return enabled ? idle : false;
}
